// Utilidades puras del organigrama (sin estado ni red).

/**
 * Códigos de tipo de componente, tal como los devuelve el back en
 * `rnhTipoEstructuraCod`. Las claves son semánticas a propósito: los códigos son de
 * una sola letra y varios se confunden entre sí (I = División, U = Unidad, O = Posta,
 * R = URESA), así que en el código se habla por nombre y la letra vive solo acá.
 *
 *   H dirección · D departamento · I división · U unidad
 *   C caps · R uresa · P puesto · O posta
 *
 * Servicio dejó de ser un tipo de componente y ya no tiene código.
 */
export const TIPO = {
    DIRECCION: "H",
    DEPARTAMENTO: "D",
    DIVISION: "I",
    UNIDAD: "U",
    // Establecimientos-hijo. CAPS es contenedor; Puesto, Posta y URESA son HOJAS.
    CAPS: "C",
    PUESTO: "P",
    POSTA: "O",
    URESA: "R",
};

// Nombre legible de cada tipo. Lo comparten el modal de gestion y el de detalle: dos
// tablas con las mismas letras se desincronizan solas.
export const NOMBRE_TIPO = {
    [TIPO.DIRECCION]: "Dirección",
    [TIPO.DEPARTAMENTO]: "Departamento",
    [TIPO.DIVISION]: "División",
    [TIPO.UNIDAD]: "Unidad",
    [TIPO.CAPS]: "CAPS",
    [TIPO.PUESTO]: "Puesto",
    [TIPO.POSTA]: "Posta",
    [TIPO.URESA]: "URESA",
};

/**
 * Tipos que PUEDEN contener hijos (aunque hoy no tengan ninguno): son los seleccionables
 * para navegar/asignar. Departamento (→ div/unid/caps/puestos/postas), División (→ unidades),
 * Unidad (→ caps/puestos/postas) y CAPS (→ puestos/postas). Hojas: Puesto, Posta y URESA.
 */
export const esContenedor = (nodo) =>
    !!nodo && (
        nodo.rnhTipoEstructuraCod === TIPO.DEPARTAMENTO ||
        nodo.rnhTipoEstructuraCod === TIPO.DIVISION ||
        nodo.rnhTipoEstructuraCod === TIPO.UNIDAD ||
        nodo.rnhTipoEstructuraCod === TIPO.CAPS
    );

// `rnhTieneHijos` llega como ENTERO. El Boolean(Number()) cubre 1/0, true/false y "1"/"0".
export const tieneHijos = (nodo) => Boolean(Number(nodo?.rnhTieneHijos));

/**
 * ¿El nodo tiene hijos para expandir AHORA? Controla el chevron y si, al
 * seleccionarlo, se piden sus hijos. Un contenedor sin hijos igual es seleccionable
 * (ver `esContenedor`), pero no muestra chevron.
 */
export const puedeExpandir = (nodo) => esContenedor(nodo) && tieneHijos(nodo);

// Descarta entradas nulas y devuelve siempre un array, aunque entre null/undefined.
export const soloValidos = (nodos) =>
    (Array.isArray(nodos) ? nodos : []).filter(Boolean);

// Niveles de ANCESTRO posibles, del más profundo al más superficial. Sirven para armar la
// "clave de padre" de un nodo; el nivel PROPIO del nodo se saltea con DESDE_PADRE.
const NIVELES_PADRE = [
    { nivel: "caps", id: (n) => n.rnhCapsId },
    { nivel: "unidad", id: (n) => n.rnhUnidadId },
    { nivel: "division", id: (n) => n.rnhDivisionId },
    { nivel: "depto", id: (n) => n.rnhDepartamentoId },
];
// Indexado por CÓDIGO de tipo (lo que trae el nodo), no por las claves de TIPO.
const DESDE_PADRE = { P: 0, O: 0, R: 0, C: 1, U: 2, I: 3, D: 4 };

/**
 * Clave del nodo EN SU PROPIO NIVEL, con la misma forma que `parentKey` devuelve.
 *
 * Con estas claves se arma el conjunto de ancestros que realmente existen en la
 * estructura del hospital. Los tipos que no son contenedores (puesto, posta, URESA) no
 * aportan ninguna: nadie puede colgar de ellos.
 */
export const nivelPropio = (n) => {
    switch (n?.rnhTipoEstructuraCod) {
        case TIPO.CAPS: return `caps:${n.rnhCapsId}`;
        case TIPO.UNIDAD: return `unidad:${n.rnhUnidadId}`;
        case TIPO.DIVISION: return `division:${n.rnhDivisionId}`;
        case TIPO.DEPARTAMENTO: return `depto:${n.rnhDepartamentoId}`;
        default: return null;
    }
};

/**
 * Clave del PADRE de un nodo: "<nivel>:<id>" del ancestro MÁS PROFUNDO presente, o `null`
 * si cuelga directo del hospital/Dirección. (El id del nivel PROPIO del nodo no cuenta.)
 * Sirve para saber de quién es hijo directo un nodo SIN depender del drill del Get, que no
 * devuelve las asignaciones directas que saltean niveles (ej. un puesto directo a una división).
 *
 * `clavesPresentes` es el conjunto de ancestros que EXISTEN en la estructura (se arma con
 * `nivelPropio`). Sin él, la busqueda corta en el primer id no nulo, y ahi esta la trampa:
 * un nodo arrastra los ids de dominio de toda su cadena, aunque esos niveles no esten
 * asignados a este hospital. Una posta colgada de un departamento pero que ademas tiene
 * `rnhUnidadId` daba "unidad:X" —una unidad que no existe en el arbol— y no matcheaba con
 * ningun target, asi que desaparecia de la columna de Asignados.
 *
 * El arbol (useArbolHospital) ya hacia esta comprobacion con su `map.get(id)`; por eso el
 * nodo SI se veia en el organigrama y no en el modal.
 */
export const parentKey = (n, clavesPresentes) => {
    for (let i = DESDE_PADRE[n?.rnhTipoEstructuraCod] ?? 0; i < NIVELES_PADRE.length; i++) {
        const id = NIVELES_PADRE[i].id(n);
        if (id == null) continue;
        const clave = `${NIVELES_PADRE[i].nivel}:${id}`;
        // Sin conjunto se conserva el comportamiento viejo: el primer id gana.
        if (!clavesPresentes || clavesPresentes.has(clave)) return clave;
    }
    return null;
};

/**
 * Clave de un TARGET (su propio nivel:id) para comparar contra `parentKey` de los nodos y
 * quedarse con sus HIJOS DIRECTOS. Dirección ("H") → null (hijos sin ancestro).
 */
export const targetKey = (t) => {
    switch (t?.tipo) {
        case TIPO.DEPARTAMENTO: return `depto:${t.departamentoId}`;
        case TIPO.DIVISION: return `division:${t.divisionId}`;
        case TIPO.UNIDAD: return `unidad:${t.unidadId}`;
        case TIPO.CAPS: return `caps:${t.capsId}`;
        default: return null; // Dirección
    }
};

// ── Componente máximo del hospital → tipos asignables ────────────────────────
//
// Vive en `consts/tipos-de-componente` y se reexporta de acá para no cambiarle el import
// a quien ya la usaba. La misma regla la necesita el modal de asignar un agente, que no
// tiene por qué importar de la carpeta del organigrama: el techo es del HOSPITAL, no de
// esta pantalla.
//
// Ahí vive también la lista de tipos en orden jerárquico, que es la que la función
// recorta. Son los mismos códigos que usa el ABM de niveles en `COMPONENTES_MAXIMOS`.
export { tiposHabilitadosPorComponenteMaximo } from "consts/tipos-de-componente";
